<template>
  <basic-dialog :show="show" @close="$emit('close')" max-width="800">
    <template v-slot:title>{{ title }}</template>
    <v-row>
      <v-col class="pb-0">
        <dates-range-picker
          :gt-date-from-string="dateFrom"
          :gt-date-to-string="dateTo"
          :outlined="outlinedInputs"
          @onRangeSelected="onDatesChange"
        ></dates-range-picker>
      </v-col>
      <v-col class="pb-0">
        <charge-types-list-select
          ref="chargeTypeSelect"
          customLabel="Name"
          v-model="name"
          :outlined="outlinedInputs"
          @onSelect="onChargeTypeSelected"
        ></charge-types-list-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="pb-0">
        <currency-list-select
          v-model="currencyCode"
          :outlined="outlinedInputs"
        ></currency-list-select>
      </v-col>
      <v-col>
        <rate-type-list-select
          v-model="rateTypeCode"
          :outlined="outlinedInputs"
          @onSelect="onRateTypeChange"
          hideNotInUse
        ></rate-type-list-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="pb-0">
        <v-text-field
          label="Per Adult"
          v-model.number="perAdult"
          type="number"
          min="0"
          :outlined="outlinedInputs"
          :disabled="itemBased"
        ></v-text-field>
      </v-col>
      <v-col class="pb-0">
        <v-text-field
          label="Per Child"
          v-model.number="perChild"
          type="number"
          min="0"
          :outlined="outlinedInputs"
          :disabled="itemBased"
        ></v-text-field>
      </v-col>
      <v-col class="pb-0">
        <v-text-field
          label="Per Infant"
          v-model.number="perInfant"
          type="number"
          min="0"
          :outlined="outlinedInputs"
          :disabled="itemBased"
        ></v-text-field>
      </v-col>
      <v-col>
        <v-text-field
          label="Per Unit"
          v-model.number="perItem"
          type="number"
          min="0"
          :outlined="outlinedInputs"
          :disabled="!itemBased"
        ></v-text-field>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-checkbox
          label="In Use"
          v-model="inUse"
          primary
          hide-details
        ></v-checkbox>
      </v-col>
    </v-row>
    <code v-if="debug"> '{{ value }} </code>
    <template v-slot:actions>
      <v-btn color="primary" @click="onSave" :disabled="!isValid">Save</v-btn>
    </template>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../shared/BasicDialog.vue";
import ChargeTypesListSelect from "../../shared/ChargeTypesListSelect.vue";
import CurrencyListSelect from "../../shared/CurrencyListSelect.vue";
import DatesRangePicker from "../../shared/DatesRangePicker.vue";
import RateTypeListSelect from "../../shared/RateTypeListSelect.vue";
export default {
  components: {
    BasicDialog,
    ChargeTypesListSelect,
    DatesRangePicker,
    CurrencyListSelect,
    RateTypeListSelect,
  },
  name: "mandatory-charge-dialog",

  props: {
    show: {
      type: Boolean,
      default: () => false,
    },
    value: {
      type: Object,
      default: () => null,
    },
  },

  data() {
    return {
      debug: false,
      name: this.value ? this.value.name : "",
      chargeTypeId: "",
      dateFrom:
        this.value && this.value.dateFrom != "" ? this.value.dateFrom : "",
      dateTo: this.value && this.value.dateTo != "" ? this.value.dateTo : "",
      perAdult: this.value ? this.value.perAdult : 0,
      perChild: this.value ? this.value.perChild : 0,
      perInfant: this.value && this.value.perInfant ? this.value.perInfant : 0,
      perItem: this.value && this.value.perItem ? this.value.perItem : 0,
      inUse: this.value ? this.value.inUse : false,
      currencyCode: this.value ? this.value.currencyCode : "",
      rateTypeCode: this.value ? this.value.rateTypeCode : "",
      itemBased: false,
    };
  },

  computed: {
    title() {
      return this.value && this.value.id ? this.name : "New Mandatory Charge";
    },
    isValid() {
      return (
        this.name &&
        this.name != "" &&
        this.dateFrom &&
        this.dateFrom != "" &&
        this.dateTo &&
        this.dateTo != "" &&
        this.currencyCode &&
        this.currencyCode != "" &&
        this.rateTypeCode != ""
      );
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    charge() {
      return {
        id: this.value && this.value.id ? this.value.id : undefined,
        name: this.name,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        perAdult: this.perAdult,
        perChild: this.perChild,
        perInfant: this.perInfant,
        perItem: this.perItem,
        inUse: this.inUse,
        currencyCode: this.currencyCode,
        chargeTypeId: this.chargeTypeId,
        rateTypeCode: this.rateTypeCode,
      };
    },
  },

  watch: {
    value(newVal) {
      this.name = newVal.name;
      this.dateFrom = newVal.dateFrom;
      this.dateTo = newVal.dateTo;
      this.perAdult = newVal.perAdult;
      this.perChild = newVal.perChild;
      this.perInfant = newVal.perInfant ? newVal.perInfant : 0;
      this.perItem = newVal.perItem ? newVal.perItem : 0;
      this.inUse = newVal.inUse;
      this.currencyCode = newVal.currencyCode;
      this.rateTypeCode = newVal.rateTypeCode;
      // #11490 - the dialog is reused between charges; without this the charge type
      // picked for the previous charge was saved onto this one
      this.chargeTypeId = this.findChargeTypeId(newVal.name);
    },
  },

  methods: {
    onRateTypeChange(payload) {
      this.itemBased = !payload.includePax;
    },
    onSave() {
      this.$emit("input", { ...this.charge });
    },
    onDatesChange(range) {
      this.dateFrom = range.dateFrom;
      this.dateTo = range.dateTo;
    },
    onChargeTypeSelected(chargeType) {
      this.chargeTypeId = chargeType.id;
    },
    findChargeTypeId(name) {
      const select = this.$refs.chargeTypeSelect;
      const found =
        select &&
        select.chargeTypesList.find((type) => type.description == name);
      return found ? found.id : "";
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/admin/service-types/MandatoryChargeDialog.vue